/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    agentId,
    conversationType,
    sortBy,
    updatedWithin,
    perPage,
    lite,
  }) {
    const params = {
      inbox_id: inboxId,
      team_id: teamId,
      agent_id: agentId,
      status,
      assignee_type: assigneeType,
      page,
      labels,
      conversation_type: conversationType,
      sort_by: sortBy,
      updated_within: updatedWithin,
      per_page: perPage,
    };

    if (lite !== undefined) {
      params.lite = lite;
    }

    return axios.get(this.url, {
      params,
    });
  }

  filter(payload) {
    const params = {
      page: payload.page,
      per_page: payload.per_page,
    };

    if (payload.lite !== undefined) {
      params.lite = payload.lite;
    }

    return axios.post(`${this.url}/filter`, payload.queryData, {
      params,
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    agentId,
    conversationType,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        agent_id: agentId,
        conversation_type: conversationType,
      },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  toggleIgnoreWebhook(conversationId) {
    return axios.post(`${this.url}/${conversationId}/toggle_ignore_webhook`);
  }

  toggleHelpme(conversationId, escalatedBy = null, escalationReason = null) {
    return axios.patch(`${this.url}/${conversationId}/helpme`, {
      escalated_by: escalatedBy,
      escalation_reason: escalationReason,
    });
  }

  markAsHelpme(conversationId, escalatedBy = null, escalationReason = null) {
    return axios.post(`${this.url}/${conversationId}/helpme/mark`, {
      escalated_by: escalatedBy,
      escalation_reason: escalationReason,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getInboxAssistant(conversationId) {
    return axios.get(`${this.url}/${conversationId}/inbox_assistant`);
  }

  delete(conversationId) {
    return axios.delete(`${this.url}/${conversationId}`);
  }
}

export default new ConversationApi();
